/**
 * BookingFlows Connect — Earnings.
 * Personal commission ledger + one-tap Stripe Express payout setup.
 * Your markup → collected by the platform at checkout → auto-transferred
 * to YOUR Stripe account (not your company's).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { apiErrorText } from '../../lib/apiError';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { connectApi } from '../../utils/apiV2';
import { ConnectShell, useConnectMe, money, CARD } from '../../components/connect/ConnectShell';
import { Wallet, Loader2, CreditCard, CheckCircle2, ArrowRight, TrendingUp, Package } from 'lucide-react';

const StatusChip = ({ status }) => {
  const map = {
    paid: 'bg-emerald-50 text-emerald-700',
    pending: 'bg-amber-50 text-amber-700',
    manual: 'bg-slate-100 text-slate-600',
    failed: 'bg-red-50 text-red-700',
  };
  const label = { paid: 'Paid out', pending: 'Processing', manual: 'Manual review', failed: 'Failed' }[status] || status;
  return <span className={`text-[11px] font-extrabold px-2 py-0.5 rounded-full ${map[status] || map.manual}`}>{label}</span>;
};

export default function ConnectEarnings() {
  const { me, refresh } = useConnectMe();
  const [searchParams] = useSearchParams();
  const [earnings, setEarnings] = useState(null);
  const [payout, setPayout] = useState(null);
  const [onboarding, setOnboarding] = useState(false);

  const load = useCallback(() => {
    connectApi.earnings().then((r) => setEarnings(r.data)).catch(() => setEarnings({ totals: {}, rows: [] }));
    connectApi.stripeStatus().then((r) => setPayout(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (searchParams.get('stripe') === 'return') {
      connectApi.stripeStatus().then((r) => {
        setPayout(r.data);
        refresh();
        if (r.data.payoutReady) toast.success('Payout account connected — you get paid automatically now');
      }).catch(() => {});
    }
  }, []);  // run once on mount (stripe return check)

  const startOnboarding = async () => {
    setOnboarding(true);
    try {
      const r = await connectApi.stripeOnboard();
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(apiErrorText(e, 'Could not start Stripe onboarding'));
      setOnboarding(false);
    }
  };

  const t = earnings?.totals || {};
  const payoutReady = payout ? payout.payoutReady : me?.affiliate?.payoutReady;

  return (
    <ConnectShell>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 py-6 sm:py-10" data-testid="connect-earnings-page">
        <div className="mb-6 cx-rise">
          <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-emerald-600 mb-2 flex items-center gap-1.5">
            <Wallet className="w-3.5 h-3.5" /> Your money — not your company's
          </p>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">Earnings</h1>
        </div>

        {/* Payout account */}
        <div className={`${CARD} p-5 mb-5 cx-rise`} style={{ animationDelay: '60ms' }} data-testid="connect-payout-card">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <span className={`w-11 h-11 rounded-2xl flex items-center justify-center shrink-0 ${payoutReady ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>
              {payoutReady ? <CheckCircle2 className="w-5 h-5" /> : <CreditCard className="w-5 h-5" />}
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-extrabold text-slate-900">
                {payoutReady ? 'Payouts active' : payout?.connected ? 'Finish your payout setup' : 'Set up personal payouts'}
              </p>
              <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                {payoutReady
                  ? 'Commissions transfer to your Stripe account automatically the moment a booking pays.'
                  : 'Connect your own Stripe account (takes ~2 minutes). Commissions accrue either way and pay out once connected.'}
              </p>
            </div>
            {!payoutReady && (
              <button
                type="button"
                disabled={onboarding}
                onClick={startOnboarding}
                data-testid="connect-stripe-onboard-btn"
                className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 text-white text-sm font-extrabold px-5 py-2.5 hover:bg-emerald-700 transition-colors disabled:opacity-60 shrink-0"
              >
                {onboarding ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                {payout?.connected ? 'Continue setup' : 'Connect Stripe'}
              </button>
            )}
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-3 gap-3 sm:gap-5 mb-6">
          {[
            { label: 'Paid out', value: money(t.paid || 0), icon: Wallet, testid: 'connect-kpi-paid' },
            { label: 'Processing', value: money(t.pending || 0), icon: TrendingUp, testid: 'connect-kpi-pending' },
            { label: 'Bookings', value: t.bookings ?? '—', icon: Package, testid: 'connect-kpi-bookings' },
          ].map((k, i) => (
            <div key={k.label} className={`${CARD} p-4 sm:p-5 cx-rise`} style={{ animationDelay: `${120 + i * 60}ms` }} data-testid={k.testid}>
              <div className="flex items-center gap-1.5 text-slate-400 mb-2">
                <k.icon className="w-3.5 h-3.5" />
                <p className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wide">{k.label}</p>
              </div>
              <p className="text-lg sm:text-2xl font-extrabold text-slate-900" style={{ fontVariantNumeric: 'tabular-nums' }}>{k.value}</p>
            </div>
          ))}
        </div>

        {/* Ledger */}
        <div className={`${CARD} overflow-hidden cx-rise`} style={{ animationDelay: '300ms' }} data-testid="connect-ledger">
          <div className="px-5 py-4 border-b border-slate-100">
            <p className="text-sm font-extrabold text-slate-900">Commission ledger</p>
          </div>
          {!earnings && <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-emerald-300" /></div>}
          {earnings && earnings.rows.length === 0 && (
            <p className="px-5 py-12 text-center text-xs text-slate-400">No commissions yet — share your flows and they'll land here the moment a booking pays.</p>
          )}
          <div className="divide-y divide-slate-50">
            {(earnings?.rows || []).map((r) => (
              <div key={r.id} className="px-5 py-3.5 flex items-center gap-3" data-testid={`connect-earning-${r.bookingCode}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-extrabold text-slate-800 truncate">{r.serviceName || 'Booking'} <span className="text-slate-400 font-mono font-normal">· {r.bookingCode}</span></p>
                  <p className="text-[11px] text-slate-400">
                    {r.operatorName}{r.flowSlug ? ` · flow ${r.flowSlug}` : ''}{r.markupPct ? ` · +${r.markupPct}%` : ''}{r.createdAt ? ` · ${new Date(r.createdAt).toLocaleDateString()}` : ''}
                  </p>
                </div>
                <p className="text-sm font-extrabold text-slate-900" style={{ fontVariantNumeric: 'tabular-nums' }}>{money(r.amount, r.currency)}</p>
                <StatusChip status={r.status} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </ConnectShell>
  );
}
